import React, { useRef, useState } from "react";
//import s from "./style.module.css";
import "./style.css";
import Vizualisation from "../../../../components/Vizualisation";
import ToggleButtons from "../../../../components/ToggleButtons";
import useVizualisation from "../../../../helpers/useVizualisation";
import { scalePoint, scaleSqrt, scaleOrdinal } from "d3-scale";
import platformsCountrySector from "../../data/platformsCountrySector.json";
import { Text } from "@visx/text";
import { useSprings, animated } from "react-spring";
import { easeSinInOut } from "d3-ease";
import ColorLegend from "../../../../components/ColorLegend/colorLegend";

const BubbleChart = ({
  data,
  title,
  note,
  source,
  numberOfPlatforms,
  platformsPerGDP,
  eCommerce,
  transportLogistics,
  tourism,
  agriculture,
  armenia,
  tunisia,
  morocco,
  sriLanka,
  peru,
  ukraine,
  philippines,
  colombia,
  bangladesh,
  egypt,
  kenya,
  southAfrica,
  malaysia,
  nigeria,
  russia,
  indonesia,
  brazil,
  eap,
  eca,
  lac,
  mena,
  sa,
  ssa,
}) => {
  const { ref, width, height, windowSize, inView } = useVizualisation();

  const margin = {
    top: 50,
    right: 40,
    bottom: 20,
    left: width < 700 ? 80 : 120,
  };

  const sectors = [
    "E-Commerce",
    "Transport/Logistics",
    "Tourism",
    "Agriculture",
  ];
  const translationsMap = {
    "E-Commerce": eCommerce,
    "Transport/Logistics": transportLogistics,
    Tourism: tourism,
    Agriculture: agriculture,
    Armenia: armenia,
    Tunisia: tunisia,
    Morocco: morocco,
    "Sri Lanka": sriLanka,
    Peru: peru,
    Ukraine: ukraine,
    Philippines: philippines,
    Colombia: colombia,
    Bangladesh: bangladesh,
    "Egypt, Arab Rep.": egypt,
    Kenya: kenya,
    "South Africa": southAfrica,
    Malaysia: malaysia,
    Nigeria: nigeria,
    Russia: russia,
    Indonesia: indonesia,
    Brazil: brazil,
    "East Asia and Pacific": eap,
    "Europe and Central Asia": eca,
    "Latin America and the Caribbean": lac,
    "Middle East and North Africa": mena,
    "South Asia": sa,
    "Sub-Saharan Africa": ssa,
  };
  const countries = [...new Set(platformsCountrySector.map((d) => d.country))];
  const regions = [
    ...new Set(platformsCountrySector.map((d) => d.region)),
  ].sort();

  const colorScale = scaleOrdinal()
    .domain(regions)
    .range([
      "#27575F",
      "#50969C",
      "#4574B1",
      "#DD6950",
      "#CCCCCC",
      "#BF5C6D",
      "#A3624C",
      "#422577",
      "#EBC147",
    ]);

  const colorScaleTranslated = scaleOrdinal()
    .domain(regions.map((d) => translationsMap[d]))
    .range([
      "#27575F",
      "#50969C",
      "#4574B1",
      "#DD6950",
      "#CCCCCC",
      "#BF5C6D",
      "#A3624C",
      "#422577",
      "#EBC147",
    ]);

  const options = [
    { value: "absolute", label: numberOfPlatforms, keyword: "Absolute" },
    {
      value: "relative",
      label: platformsPerGDP,
      keyword: "Relative",
    },
  ];

  const [selectedValue, setSelectedValue] = useState("absolute");

  const handleChange = (e) => {
    setSelectedValue(e.target.value);
  };

  const chartWidth = width - margin.left - margin.right;
  const chartHeight =
    width < 700
      ? 600 - margin.top - margin.bottom
      : 400 - margin.top - margin.bottom;

  const xScale = scalePoint()
    .domain(countries)
    .range([0, chartWidth])
    .padding(0.5);

  const yScale = scalePoint()
    .domain(sectors)
    .range([0, chartHeight])
    .padding(0.5);

  if (width < 700) {
    xScale.domain(sectors);
    yScale.domain(countries).range([chartHeight, 0]);
  }

  const sizeScale = scaleSqrt().range([0, 40]);

  if (selectedValue === "absolute") {
    sizeScale.domain([0, 112]);
  }
  if (selectedValue === "relative") {
    sizeScale.domain([0, 28]);
  }

  const circleStyles = useSprings(
    platformsCountrySector.length,
    platformsCountrySector.map((d, i) => {
      return {
        config: {
          duration: 1000,
          easing: easeSinInOut,
        },
        r: selectedValue === "absolute" ? sizeScale(d.n) : sizeScale(d.pergdp),
        opacity: selectedValue === "absolute" ? 0.8 : 0,
      };
    })
  );

  const horizontalGridData = width < 700 ? sectors : countries;
  const verticalGridData = width < 700 ? countries : sectors;

  return (
    <>
      <ToggleButtons
        options={options}
        onChange={setSelectedValue}
        selected={selectedValue}
      />
      <ColorLegend colorscale={colorScaleTranslated}></ColorLegend>
      <Vizualisation
        title={title}
        note={note}
        source={source}
        ref={ref}
        size="fullwidth"
      >
        <svg width={width} height={width < 700 ? 600 : 400}>
          <g
            className="chart"
            transform={`translate(${margin.left}, ${margin.top})`}
          >
            <g className="grid">
              {horizontalGridData.map((d) => {
                return (
                  <g className="grid-vertical" opacity={0.5}>
                    <line
                      x1={xScale(d)}
                      x2={xScale(d)}
                      y1={0}
                      y2={chartHeight}
                      stroke={"#eeeeee"}
                      strokeWidth={1}
                    ></line>
                  </g>
                );
              })}
              {verticalGridData.map((d) => {
                return (
                  <g className="grid-horizontal" opacity={0.5}>
                    <line
                      x1={0}
                      x2={chartWidth}
                      y1={yScale(d)}
                      y2={yScale(d)}
                      stroke={"#eeeeee"}
                      strokeWidth={1}
                    ></line>
                  </g>
                );
              })}
            </g>
            <g>
              {platformsCountrySector.map((d, i) => {
                return (
                  <>
                    <animated.circle
                      key={"circle-" + d.country + d.sector}
                      cx={width > 699 ? xScale(d.country) : xScale(d.sector)}
                      cy={width > 699 ? yScale(d.sector) : yScale(d.country)}
                      r={circleStyles[i].r}
                      fill={colorScale(d.region)}
                      fillOpacity={0.75}
                      stroke={colorScale(d.region)}
                      strokeWidth={1.5}
                      strokeOpacity={1}
                    ></animated.circle>
                    {d.n > 1 ? (
                      <animated.text
                        x={width > 699 ? xScale(d.country) : xScale(d.sector)}
                        y={width > 699 ? yScale(d.sector) : yScale(d.country)}
                        verticalAnchor={"middle"}
                        textAnchor={"middle"}
                        paintOrder={"stroke"}
                        opacity={circleStyles[i].opacity}
                        stroke={"white"}
                        strokeWidth={"2px"}
                        strokeLinecap={"butt"}
                        strokeLinejoin={"miter"}
                        fontSize={"11px"}
                        fontWeight={"bold"}
                        dy={4}
                      >
                        {d.n}
                      </animated.text>
                    ) : (
                      ""
                    )}
                  </>
                );
              })}
            </g>
            {horizontalGridData.map((d) => {
              return (
                <g className="axis-labels-x">
                  <Text
                    x={xScale(d)}
                    y={-20}
                    verticalAnchor={"end"}
                    textAnchor={"middle"}
                    width={xScale.bandwidth() - 20}
                    fontSize={"10px"}
                    fontWeight={"bold"}
                    fill={
                      width > 699
                        ? colorScale(
                            platformsCountrySector.filter(
                              (cntr) => cntr.country === d
                            )[0].region
                          )
                        : "#333333"
                    }
                    paintOrder={"stroke"}
                    stroke={"white"}
                    strokeWidth={"2px"}
                    strokeLinecap={"butt"}
                    strokeLinejoin={"miter"}
                  >
                    {translationsMap[d]}
                  </Text>
                </g>
              );
            })}
            {verticalGridData.map((d) => {
              return (
                <g className="axis-labels-y">
                  <Text
                    x={-4}
                    y={yScale(d)}
                    verticalAnchor={"middle"}
                    textAnchor={"end"}
                    fontSize={"10px"}
                    fontWeight={"bold"}
                    fill={
                      width < 700
                        ? colorScale(
                            platformsCountrySector.filter(
                              (cntr) => cntr.country === d
                            )[0].region
                          )
                        : "#333333"
                    }
                    paintOrder={"stroke"}
                    stroke={"white"}
                    strokeWidth={"2px"}
                    strokeLinecap={"butt"}
                    strokeLinejoin={"miter"}
                    width={width < 700 ? 80 : 120}
                  >
                    {translationsMap[d]}
                  </Text>
                </g>
              );
            })}
          </g>
        </svg>
      </Vizualisation>
    </>
  );
};

export default BubbleChart;
